import { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { getWeeklyReportsPath, getStudiesPath } from "../services/firestorePaths";
import { reportCoordinatorName } from "../services/weeklyReportCoordinator";
import { reportPeriodCounts } from "../services/weeklyReportModel";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  ArrowLeft,
  BarChart3,
  TrendingUp,
  Activity,
  Sparkles,
  AlertTriangle,
  Percent,
  Layers,
  CheckCircle2,
  ListChecks,
  Users,
  FlaskConical,
} from "lucide-react";

const CHART_COLORS = ["#A78BFA", "#60A5FA", "#34D399", "#FBBF24", "#F472B6", "#22D3EE", "#FB923C", "#A3E635"];

const tooltipStyles = {
  contentStyle: {
    background: "rgba(11, 8, 32, 0.92)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: "12px",
    backdropFilter: "blur(14px)",
    color: "#e2e8f0",
    fontSize: "12px",
    boxShadow: "0 10px 30px -10px rgba(124,58,237,0.5)",
  },
  itemStyle: { color: "#e2e8f0" },
  labelStyle: {
    color: "#c4b5fd",
    fontSize: "10px",
    textTransform: "uppercase",
    letterSpacing: "0.15em",
    marginBottom: "4px",
  },
  cursor: { fill: "rgba(139,92,246,0.08)" },
};

const axisStyle = {
  stroke: "rgba(255,255,255,0.15)",
  tick: { fill: "rgba(226,232,240,0.7)", fontSize: 11 },
};

const legendStyle = { color: "rgba(226,232,240,0.8)", fontSize: "11px" };

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img src="/bg_ai.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

const Card = ({ title, subtitle, icon, children, className = "" }) => (
  <div className={`relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-4 overflow-hidden ${className}`}>
    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.05] via-transparent to-blue-500/[0.05] pointer-events-none" />
    <div className="relative">
      <div className="mb-3 flex items-center gap-2">
        {icon && (
          <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center">
            {icon}
          </div>
        )}
        <div className="min-w-0">
          <h3 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white leading-tight">{title}</h3>
          {subtitle && <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{subtitle}</p>}
        </div>
        <span className="ml-auto h-px flex-1 bg-white/5" />
      </div>
      {children}
    </div>
  </div>
);

const KpiTile = ({ label, value, icon, accent, valueClass = "text-white" }) => (
  <div className="relative overflow-hidden rounded-2xl border border-white/10 hover:border-purple-300/30 bg-white/[0.03] backdrop-blur-xl px-3.5 py-2.5 transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-12px_rgba(139,92,246,0.4)] group">
    <div className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-60 pointer-events-none`} />
    <div className="relative flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-300/80 font-medium">{label}</div>
        <div className={`text-2xl font-bold tracking-tight leading-tight truncate ${valueClass}`}>{value}</div>
      </div>
      <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:scale-105 transition shrink-0">
        {icon}
      </div>
    </div>
  </div>
);

// Status → colour mapping (site palette)
const STATUS_META = {
  "ACTIVE":    { label: "Active",      color: "#34D399", glow: "rgba(52,211,153,0.5)"  }, // emerald
  "ACTIVE/NR": { label: "Active (NR)", color: "#FBBF24", glow: "rgba(251,191,36,0.5)"  }, // amber
  "STARTUP":   { label: "Startup",     color: "#60A5FA", glow: "rgba(96,165,250,0.5)"  }, // blue
  "CLOSED":    { label: "Closed",      color: "#94A3B8", glow: "rgba(148,163,184,0.4)" }, // slate
};
// Studies that appear in reports but whose status can't be resolved fall back
// to "Closed" — they were active when reported and are likely now archived.
const getStudyMeta = (status) =>
  STATUS_META[(status || "").toUpperCase()] || STATUS_META["CLOSED"];

const parseWeekOf = (weekOf) => {
  if (!weekOf || !weekOf.includes("-W")) return { year: 0, week: 0 };
  const [year, wk] = weekOf.split("-W");
  return { year: +year, week: +wk };
};

const StatisticsPage = () => {
  const [reports, setReports] = useState([]);
  const [studyStatusMap, setStudyStatusMap] = useState({}); // studyId → STATUS string
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReports = async () => {
      const snapshot = await getDocs(collection(db, getWeeklyReportsPath()));
      setReports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchReports();
  }, []);

  useEffect(() => {
    const fetchStatuses = async () => {
      const snapshot = await getDocs(collection(db, getStudiesPath()));
      const map = {};
      snapshot.docs.forEach(doc => {
        const d = doc.data();
        if (!d.STATUS) return;
        // Index by both the STUDY_ID field and the Firestore doc ID so weekly
        // reports that stored either value as studyId resolve to the right status.
        if (d.STUDY_ID) map[d.STUDY_ID] = d.STATUS;
        map[doc.id] = d.STATUS;
      });
      setStudyStatusMap(map);
    };
    fetchStatuses();
  }, []);

  /* ---------- AGGREGATE ---------- */
  const {
    totalEnrollments,
    totalScreenings,
    totalPreScreens,
    totalScreenFailures,
    totalFollowUps,
    studyTotals,
    coordinatorTotals,
    weeklyTrend,
  } = useMemo(() => {
    let totalEnrollments = 0, totalScreenings = 0, totalPreScreens = 0,
      totalScreenFailures = 0, totalFollowUps = 0;
    const studyTotals = {};
    const coordinatorTotals = {};
    const weeklyTrend = {};

    reports.forEach(report => {
      // Same credit rule as Admin Reports: an admin-assigned name is used for
      // a report saved with no coordinator name.
      const coord = reportCoordinatorName(report) || "Unknown";
      const weekOf = report.weekOf || "Unknown";
      const submittedTs = report.submittedAt?.toDate?.()?.getTime?.() ?? 0;

      if (!coordinatorTotals[coord]) coordinatorTotals[coord] = { enrollments: 0, screenings: 0, followUps: 0, lastSeen: 0 };
      coordinatorTotals[coord].lastSeen = Math.max(coordinatorTotals[coord].lastSeen, submittedTs);
      if (!weeklyTrend[weekOf]) weeklyTrend[weekOf] = { week: weekOf, enrollments: 0, screenings: 0 };

      (report.studies || []).forEach(s => {
        const id = s.studyId || "Unknown";
        if (!studyTotals[id]) studyTotals[id] = { name: s.name || id, preScreens: 0, screenings: 0, screenFailures: 0, enrollments: 0, followUps: 0, lastSeen: 0 };
        studyTotals[id].lastSeen = Math.max(studyTotals[id].lastSeen, submittedTs);

        studyTotals[id].preScreens    += s.preScreens    || 0;
        studyTotals[id].screenings    += s.screenings    || 0;
        studyTotals[id].screenFailures+= s.screenFailures|| 0;
        studyTotals[id].enrollments   += s.enrollments   || 0;
        studyTotals[id].followUps     += s.followUps     || 0;

        coordinatorTotals[coord].enrollments += s.enrollments || 0;
        coordinatorTotals[coord].screenings  += s.screenings  || 0;
        coordinatorTotals[coord].followUps   += s.followUps   || 0;

        weeklyTrend[weekOf].enrollments += s.enrollments || 0;
        weeklyTrend[weekOf].screenings  += s.screenings  || 0;

        totalEnrollments    += s.enrollments    || 0;
        totalScreenings     += s.screenings     || 0;
        totalPreScreens     += s.preScreens     || 0;
        totalScreenFailures += s.screenFailures || 0;
        totalFollowUps      += s.followUps      || 0;
      });

      // New-format reports: pre-screens / follow-ups are for the whole period,
      // not per study, so they count in the totals but not in any study's row.
      const period = reportPeriodCounts(report);
      totalPreScreens += period.preScreens;
      totalFollowUps += period.followUps;
      coordinatorTotals[coord].followUps += period.followUps;
    });

    return { totalEnrollments, totalScreenings, totalPreScreens, totalScreenFailures, totalFollowUps, studyTotals, coordinatorTotals, weeklyTrend };
  }, [reports]);

  /* ---------- DERIVED ---------- */
  const conversionRate = totalScreenings > 0
    ? (totalEnrollments / totalScreenings * 100).toFixed(1)
    : "—";
  const failureRate = totalPreScreens > 0
    ? (totalScreenFailures / totalPreScreens * 100).toFixed(1)
    : "—";

  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;

  // Enrollments by study — exclude 0-enrollment studies; attach status colour from studies collection
  const studyEnrollmentData = Object.entries(studyTotals)
    .map(([id, d]) => {
      const status = studyStatusMap[id] || "";
      const meta = getStudyMeta(status);
      return { studyId: id, name: d.name, enrollments: d.enrollments, status, color: meta.color, glow: meta.glow };
    })
    .filter(d => d.enrollments > 0)
    .sort((a, b) => b.enrollments - a.enrollments);

  // Coordinator breakdown — only coordinators with a submission in the last 30 days
  const coordinatorData = Object.entries(coordinatorTotals)
    .map(([name, d]) => ({ name, ...d }))
    .filter(c => c.enrollments > 0 && c.lastSeen >= thirtyDaysAgo)
    .sort((a, b) => b.enrollments - a.enrollments);

  // Weekly trend sorted by week
  const trendData = Object.values(weeklyTrend).sort((a, b) => {
    const A = parseWeekOf(a.week);
    const B = parseWeekOf(b.week);
    return A.year !== B.year ? A.year - B.year : A.week - B.week;
  });

  // Full per-study table
  const studyTableData = Object.entries(studyTotals)
    .map(([id, d]) => ({
      id,
      name: d.name,
      preScreens: d.preScreens,
      screenFailures: d.screenFailures,
      screenings: d.screenings,
      enrollments: d.enrollments,
      followUps: d.followUps,
      convRate: d.screenings > 0 ? (d.enrollments / d.screenings * 100).toFixed(1) : "—",
      failRate: d.preScreens > 0 ? (d.screenFailures / d.preScreens * 100).toFixed(1) : "—",
    }))
    .sort((a, b) => b.enrollments - a.enrollments);

  // Highlight cards
  const topStudy = studyTableData[0] || null;
  const highestFailStudy = [...studyTableData]
    .filter(s => s.preScreens > 0)
    .sort((a, b) => parseFloat(b.failRate) - parseFloat(a.failRate))[0] || null;

  /* ---------- UI ---------- */
  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      <div className="relative px-3 sm:px-4 lg:px-6 py-4 max-w-7xl mx-auto space-y-3">

        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">Analytics</div>
              <h1 className="text-base sm:text-lg font-semibold tracking-tight text-white leading-tight truncate">
                Site Performance Statistics
              </h1>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-[10px] uppercase tracking-wider text-slate-300">
            <Sparkles className="w-3 h-3 text-purple-300" />
            {reports.length} reports · {studyTableData.length} studies
          </div>
        </div>

        {/* KPI tiles */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          <KpiTile
            label="Total Enrollments"
            value={totalEnrollments}
            icon={<CheckCircle2 className="w-4 h-4 text-white/80" />}
            accent="from-emerald-500/20 to-teal-500/20"
            valueClass="text-emerald-300"
          />
          <KpiTile
            label="Total Screenings"
            value={totalScreenings}
            icon={<Layers className="w-4 h-4 text-white/80" />}
            accent="from-purple-500/20 to-indigo-500/20"
          />
          <KpiTile
            label="Total Follow-ups"
            value={totalFollowUps}
            icon={<ListChecks className="w-4 h-4 text-white/80" />}
            accent="from-blue-500/20 to-cyan-500/20"
            valueClass="text-blue-300"
          />
          <KpiTile
            label="Screen Failures"
            value={totalScreenFailures}
            icon={<AlertTriangle className="w-4 h-4 text-white/80" />}
            accent="from-rose-500/20 to-pink-500/20"
            valueClass="text-rose-300"
          />
        </section>

        {/* Rate row */}
        <section className="grid grid-cols-2 gap-2.5">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-xl px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center shrink-0">
              <TrendingUp className="w-4 h-4 text-emerald-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Conversion Rate</div>
              <div className="text-xl font-bold text-white">
                {conversionRate === "—" ? "—" : `${conversionRate}%`}
              </div>
              <div className="text-[10px] text-slate-500">Enrollments ÷ Screenings</div>
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-xl px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center shrink-0">
              <Percent className="w-4 h-4 text-rose-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Screen Failure Rate</div>
              <div className="text-xl font-bold text-rose-300">
                {failureRate === "—" ? "—" : `${failureRate}%`}
              </div>
              <div className="text-[10px] text-slate-500">Screen Failures ÷ Pre-screens</div>
            </div>
          </div>
        </section>

        {/* Highlight cards */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <Card title="Top Enrolling Study" subtitle="Highest total enrollments across all weeks" icon={<Activity className="w-3.5 h-3.5 text-emerald-300" />}>
            {topStudy ? (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/30 to-teal-500/30 border border-emerald-300/30 flex items-center justify-center shrink-0">
                  <FlaskConical className="w-5 h-5 text-emerald-200" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{topStudy.id}</div>
                  <div className="text-sm font-semibold text-white truncate">{topStudy.name}</div>
                  <div className="text-xs text-emerald-300 font-semibold">{topStudy.enrollments} enrollments · {topStudy.convRate === "—" ? "—" : `${topStudy.convRate}% conversion`}</div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400">No data yet.</p>
            )}
          </Card>

          <Card title="Highest Screen Failure Rate" subtitle="Study where most pre-screens are failing" icon={<AlertTriangle className="w-3.5 h-3.5 text-rose-300" />}>
            {highestFailStudy ? (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500/30 to-pink-500/30 border border-rose-300/30 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-rose-200" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{highestFailStudy.id}</div>
                  <div className="text-sm font-semibold text-white truncate">{highestFailStudy.name}</div>
                  <div className="text-xs text-rose-300 font-semibold">{highestFailStudy.failRate}% failure rate · {highestFailStudy.screenFailures} of {highestFailStudy.preScreens} pre-screens failed</div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400">No data yet.</p>
            )}
          </Card>
        </section>

        {/* Charts */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">

          {/* Enrollments by Study */}
          <Card
            title="Enrollments by Study"
            subtitle="Cumulative across all submitted weeks"
            icon={<BarChart3 className="w-3.5 h-3.5 text-purple-300" />}
            className="lg:col-span-2"
          >
            {studyEnrollmentData.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">No data yet.</p>
            ) : (
              <>
                {/* Status legend */}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mb-4">
                  {Object.values(STATUS_META).map(({ label, color, glow }) => (
                    <span key={label} className="flex items-center gap-1.5 text-[10px] text-slate-400">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-sm"
                        style={{ background: color, boxShadow: `0 0 6px ${glow}` }}
                      />
                      {label}
                    </span>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={Math.max(200, studyEnrollmentData.length * 44)}>
                  <BarChart
                    layout="vertical"
                    data={studyEnrollmentData}
                    margin={{ top: 4, right: 32, bottom: 4, left: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                    <XAxis type="number" {...axisStyle} />
                    <YAxis
                      type="category"
                      dataKey="studyId"
                      {...axisStyle}
                      width={72}
                      tick={{ fill: "rgba(196,181,253,0.85)", fontSize: 11 }}
                    />
                    <Tooltip
                      {...tooltipStyles}
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        const meta = getStudyMeta(d.status);
                        return (
                          <div style={tooltipStyles.contentStyle}>
                            <p style={{ ...tooltipStyles.labelStyle, color: d.color }}>{meta.label}</p>
                            <p className="text-[11px] text-slate-300 font-medium">{d.studyId} — {d.name}</p>
                            <p style={tooltipStyles.itemStyle} className="font-semibold mt-0.5">
                              {d.enrollments} enrollments
                            </p>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="enrollments" radius={[0, 6, 6, 0]}>
                      {studyEnrollmentData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color}
                          style={{ filter: `drop-shadow(0 0 4px ${entry.glow})` }}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}
          </Card>

          {/* Weekly Site Trend */}
          <Card
            title="Weekly Site Trend"
            subtitle="Enrollments & screenings over time"
            icon={<TrendingUp className="w-3.5 h-3.5 text-emerald-300" />}
          >
            {trendData.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">No data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trendData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                  <defs>
                    <linearGradient id="enrollLine" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#34D399" />
                      <stop offset="100%" stopColor="#22D3EE" />
                    </linearGradient>
                    <linearGradient id="screenLine" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#A78BFA" />
                      <stop offset="100%" stopColor="#818CF8" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="week" {...axisStyle} />
                  <YAxis {...axisStyle} />
                  <Tooltip {...tooltipStyles} />
                  <Legend wrapperStyle={legendStyle} />
                  <Line type="monotone" dataKey="screenings" stroke="url(#screenLine)" strokeWidth={2} dot={{ fill: "#A78BFA", r: 2.5, strokeWidth: 0 }} activeDot={{ r: 4 }} name="Screenings" />
                  <Line type="monotone" dataKey="enrollments" stroke="url(#enrollLine)" strokeWidth={2.5} dot={{ fill: "#34D399", r: 2.5, strokeWidth: 0 }} activeDot={{ r: 4 }} name="Enrollments" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Coordinator Breakdown */}
          <Card
            title="Enrollments by Coordinator"
            subtitle="Total enrolled across all studies"
            icon={<Users className="w-3.5 h-3.5 text-blue-300" />}
          >
            {coordinatorData.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">No coordinators active in the last 30 days.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  layout="vertical"
                  data={coordinatorData}
                  margin={{ top: 4, right: 24, bottom: 4, left: 8 }}
                >
                  <defs>
                    <linearGradient id="coordBarFill" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#34D399" stopOpacity={0.95} />
                      <stop offset="100%" stopColor="#22D3EE" stopOpacity={0.9} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                  <XAxis type="number" {...axisStyle} />
                  <YAxis type="category" dataKey="name" {...axisStyle} width={72} tick={{ fill: "rgba(196,181,253,0.85)", fontSize: 11 }} />
                  <Tooltip {...tooltipStyles} />
                  <Bar dataKey="enrollments" fill="url(#coordBarFill)" radius={[0, 6, 6, 0]} name="Enrollments" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Screening Funnel */}
          <Card
            title="Patient Journey Funnel"
            subtitle="Site-wide aggregate across all studies and weeks"
            icon={<Activity className="w-3.5 h-3.5 text-purple-300" />}
            className="lg:col-span-2"
          >
            {totalPreScreens === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">No data yet.</p>
            ) : (
              <div className="space-y-3">
                {[
                  { label: "Pre-screens", value: totalPreScreens, color: "from-violet-400 to-purple-400", glow: "rgba(167,139,250,0.4)", pct: 100 },
                  { label: "Screenings", value: totalScreenings, color: "from-blue-400 to-indigo-400", glow: "rgba(96,165,250,0.4)", pct: totalPreScreens > 0 ? (totalScreenings / totalPreScreens * 100) : 0 },
                  { label: "Enrollments", value: totalEnrollments, color: "from-emerald-400 to-teal-400", glow: "rgba(52,211,153,0.4)", pct: totalPreScreens > 0 ? (totalEnrollments / totalPreScreens * 100) : 0 },
                  { label: "Follow-ups", value: totalFollowUps, color: "from-cyan-400 to-sky-400", glow: "rgba(34,211,238,0.4)", pct: totalPreScreens > 0 ? (totalFollowUps / totalPreScreens * 100) : 0 },
                  { label: "Screen Failures", value: totalScreenFailures, color: "from-rose-400 to-pink-400", glow: "rgba(251,113,133,0.4)", pct: totalPreScreens > 0 ? (totalScreenFailures / totalPreScreens * 100) : 0 },
                ].map(({ label, value, color, glow, pct }) => (
                  <div key={label}>
                    <div className="flex items-center justify-between text-[10px] mb-1.5">
                      <span className="uppercase tracking-wider text-slate-400 font-medium">{label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-300 font-semibold">{value.toLocaleString()}</span>
                        <span className="text-slate-500">{pct.toFixed(1)}% of pre-screens</span>
                      </div>
                    </div>
                    <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className={`h-full rounded-full bg-gradient-to-r ${color} transition-all duration-700`}
                        style={{ width: `${Math.min(100, pct)}%`, boxShadow: `0 0 10px ${glow}` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

        </section>

        {/* Per-study breakdown table */}
        <Card
          title="Study Breakdown"
          subtitle="All metrics per study, sorted by total enrollments"
          icon={<FlaskConical className="w-3.5 h-3.5 text-purple-300" />}
        >
          {studyTableData.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No data yet.</p>
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-400 border-b border-white/5">
                    <th className="px-3 py-2">Study</th>
                    <th className="px-3 py-2 text-center">Pre-screens</th>
                    <th className="px-3 py-2 text-center">Screen Fail</th>
                    <th className="px-3 py-2 text-center">Screenings</th>
                    <th className="px-3 py-2 text-center">Enrollments</th>
                    <th className="px-3 py-2 text-center">Follow-ups</th>
                    <th className="px-3 py-2 text-center">Conv. Rate</th>
                    <th className="px-3 py-2 text-center">Fail Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {studyTableData.map((s, i) => (
                    <tr key={s.id} className={`border-t border-white/5 hover:bg-white/[0.03] transition ${i === 0 ? "bg-emerald-500/[0.04]" : ""}`}>
                      <td className="px-3 py-2.5">
                        <span className="font-mono text-purple-300/80 mr-1.5">{s.id}</span>
                        <span className="text-slate-300">{s.name !== s.id ? s.name : ""}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center text-slate-300 font-mono">{s.preScreens}</td>
                      <td className="px-3 py-2.5 text-center text-rose-300 font-mono">{s.screenFailures}</td>
                      <td className="px-3 py-2.5 text-center text-slate-200 font-mono">{s.screenings}</td>
                      <td className="px-3 py-2.5 text-center text-emerald-300 font-bold font-mono">{s.enrollments}</td>
                      <td className="px-3 py-2.5 text-center text-blue-300 font-mono">{s.followUps}</td>
                      <td className="px-3 py-2.5 text-center font-mono">
                        <span className={s.convRate === "—" ? "text-slate-500" : "text-white"}>
                          {s.convRate === "—" ? "—" : `${s.convRate}%`}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center font-mono">
                        <span className={
                          s.failRate === "—" ? "text-slate-500"
                          : parseFloat(s.failRate) > 50 ? "text-rose-300 font-semibold"
                          : parseFloat(s.failRate) > 25 ? "text-amber-300"
                          : "text-slate-300"
                        }>
                          {s.failRate === "—" ? "—" : `${s.failRate}%`}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="h-6" />
      </div>
    </div>
  );
};

export default StatisticsPage;
